import React, { createRef, FC, useState } from 'react';
import { useSelector } from 'react-redux';

// Shared UI Library Components
import {
  DiscardModal,
  TabsElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './rates.scss';

// Components
import { PricePerMinute, TPricePerMinuteRef } from '../price-per-minute/price-per-minute';
import { PricePerMessage, TPricePerMessageRef } from '../price-per-message/price-per-message';
import { CostPerSession, TCostPerSessionRef } from '../cost-per-session/cost-per-session';

// Store entities
import { getCurrentCounsellorState } from '../../../../store/current-counsellor/current-counsellor.selectors';
import { getCoreState } from '../../../../store/core/core.selectors';
import { getCurrencyState } from '../../../../store/currency/currency.selectors';
import { getUserState } from '../../../../store/user/user.selectors';
import { FreeServices } from '../free-services/free-services';

type TRatesProps = {};

type TTabKeys = 'perMinute' | 'perMessage' | 'cost';

const defaultActiveKey = 'perMinute';

export const Rates: FC<TRatesProps> = (
  {}: TRatesProps
) => {
  const pricePerMinuteRef = createRef<TPricePerMinuteRef>();
  const pricePerMessageRef = createRef<TPricePerMessageRef>();
  const costPerSessionRef = createRef<TCostPerSessionRef>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<TTabKeys>(defaultActiveKey);
  const [nextKey, setNextKey] = useState<TTabKeys | null>(null);

  const { counsellor, session_rates, free_minutes } = useSelector(getCurrentCounsellorState);
  const { companyFee } = useSelector(getCoreState);
  const { currentCurrency } = useSelector(getCurrencyState);
  const { user } = useSelector(getUserState);

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      toggleDiscardModal();
      resetRefState();
      setActiveKey(nextKey ?? defaultActiveKey);
      setNextKey(null);
      return;
    }
    toggleDiscardModal();
  }

  const resetRefState = (): void => {
    if (activeKey === 'perMinute') {
      pricePerMinuteRef.current?.resetState();
      return;
    }
    if (activeKey === 'perMessage') {
      pricePerMessageRef.current?.resetState();
      return;
    }
    costPerSessionRef.current?.resetState();
  }

  const onTabClickHandler = (key: string, event: any) => {
    event.preventDefault();
    setNextKey(key as TTabKeys);

    if (
      pricePerMinuteRef.current?.isHasChanges() ||
      pricePerMessageRef.current?.isHasChanges() ||
      costPerSessionRef.current?.isHasChanges()
    ) {
      toggleDiscardModal();
      return;
    }

    setActiveKey(key as TTabKeys);
  }

  return (
    <div className="rates">

      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />

      <div className="rates--header">
        <div className="header-title">
          Pricing Settings
        </div>
      </div>

      { (counsellor) && <div className="rates--free-services">
        <FreeServices counsellor={ counsellor }
                      free_minutes={ free_minutes }
                      is_stripe_connected={ !!user?.is_stripe_connected }
        />
      </div> }

      {
        (counsellor && !!companyFee.values && currentCurrency) && <div className="rates--body">
          <TabsElement
            activeKey={ activeKey }
            onTabClick={ onTabClickHandler }
            items={ [
              {
                key: 'perMinute',
                label: 'Price per Minute',
                children: <PricePerMinute ref={ pricePerMinuteRef } counsellor={ counsellor }
                                          is_stripe_connected={ !!user?.is_stripe_connected }
                                          free_minutes={ free_minutes }
                                          currency={ currentCurrency }
                                          percentFee={ Number(companyFee.values?.call_percent_fee) }
                                          transactionFee={ Number(companyFee.values?.call_transaction_fee) }
                />,
              },
              {
                key: 'cost',
                label: 'Cost per Session',
                children: <CostPerSession ref={ costPerSessionRef } counsellor={ counsellor }
                                          currency={ currentCurrency }
                                          is_stripe_connected={ !!user?.is_stripe_connected }
                                          session_rates={ session_rates }
                                          percentFee={ Number(companyFee.values?.call_percent_fee) }
                                          transactionFee={ Number(companyFee.values?.call_transaction_fee) }
                />,
              },
              {
                key: 'perMessage',
                label: 'Price per Request',
                children: <PricePerMessage ref={ pricePerMessageRef } counsellor={ counsellor }
                                           currency={ currentCurrency }
                                           is_stripe_connected={ !!user?.is_stripe_connected }
                                           free_minutes={ free_minutes }
                                           percentFee={ Number(companyFee.values?.message_percent_fee) }
                                           transactionFee={ Number(companyFee.values?.call_transaction_fee) }
                />,
              },
            ] }/>
        </div>
      }
    </div>
  );
}
